import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Phone, MapPin, FileText, CreditCard, Check, ChevronLeft, Loader2, Navigation } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

const STEPS = [
  { label: 'البيانات الأساسية', icon: User },
  { label: 'الموقع', icon: MapPin },
  { label: 'تفاصيل إضافية', icon: FileText },
  { label: 'مراجعة وإنشاء', icon: Check },
];

export default function NewCustomer() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [created, setCreated] = useState(null);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    mobile: '',
    email: '',
    street: '',
    city: '',
    vat: '',
    comment: '',
    credit_limit: '',
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error('متصفحك لا يدعم تحديد الموقع'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await resp.json();
          const city = data.address?.city || data.address?.town || data.address?.village || '';
          const road = data.address?.road || '';
          set('city', city);
          if (road) set('street', road);
          toast.success('تم تحديد موقعك');
        } catch {
          toast.error('تعذّر تحديد العنوان');
        }
        setLocating(false);
      },
      () => { toast.error('لم يتم السماح بالوصول للموقع'); setLocating(false); }
    );
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('الاسم مطلوب'); return; }
    if (!form.phone.trim()) { toast.error('الهاتف مطلوب'); return; }
    setSaving(true);
    const res = await api.post('/customers', {
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
      email: form.email.trim() || undefined,
      street: form.street.trim() || undefined,
      city: form.city.trim() || undefined,
      vat: form.vat.trim() || undefined,
      comment: form.comment.trim() || undefined,
      credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : undefined });
    if (res?.success) {
      toast.success('تم إنشاء العميل بنجاح');
      setCreated(res.data);
    } else {
      toast.error(res?.error || 'فشل إنشاء العميل');
    }
    setSaving(false);
  };

  // Success screen
  if (created) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 gap-6" dir="rtl">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
          <Check className="w-10 h-10 text-emerald-600" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground">تم إنشاء العميل!</h2>
          <p className="text-muted-foreground mt-1">{created.name}</p>
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <Button className="w-full bg-primary" onClick={() => navigate(`/customers/${created.id}`)}>
            عرض بيانات العميل
          </Button>
          <Button variant="outline" className="w-full"
            onClick={() => navigate(`/orders/new?customer_id=${created.id}&customer_name=${encodeURIComponent(created.name)}`)}>
            إنشاء طلب جديد
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/customers')}>
            العودة للقائمة
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-32 space-y-5" dir="rtl">
      {/* Step indicator */}
      <div className="flex gap-1.5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = step === i + 1;
          const isDone = step > i + 1;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className={`h-1.5 rounded-full w-full transition-all ${isDone ? 'bg-primary' : isActive ? 'bg-primary/60' : 'bg-muted'}`} />
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isDone ? 'bg-primary' : isActive ? 'bg-primary/20 border-2 border-primary' : 'bg-muted'}`}>
                {isDone ? <Check className="w-3 h-3 text-white" /> : <Icon className={`w-3 h-3 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />}
              </div>
              <p className={`text-xs text-center leading-tight ${isActive ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-foreground">البيانات الأساسية</h2>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">اسم العميل *</label>
              <Input value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="مثال: بقالة أبو علي" className="mt-1 rounded-xl" autoFocus />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">هاتف *</label>
              <Input value={form.phone} onChange={e => set('phone', e.target.value)}
                placeholder="010xxxxxxxx" type="tel" className="mt-1 rounded-xl" dir="ltr" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">جوال (اختياري)</label>
              <Input value={form.mobile} onChange={e => set('mobile', e.target.value)}
                placeholder="011xxxxxxxx" type="tel" className="mt-1 rounded-xl" dir="ltr" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">البريد الإلكتروني (اختياري)</label>
              <Input value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="example@email.com" type="email" className="mt-1 rounded-xl" dir="ltr" />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Location */}
      {step === 2 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-foreground">الموقع</h2>
          <Button onClick={detectLocation} variant="outline" className="w-full gap-2" disabled={locating}>
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
            {locating ? 'جاري تحديد موقعك...' : 'استخدم موقعي الحالي'}
          </Button>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">الشارع / العنوان</label>
              <Input value={form.street} onChange={e => set('street', e.target.value)}
                placeholder="مثال: شارع النيل، رقم 5" className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">المدينة / المنطقة</label>
              <Input value={form.city} onChange={e => set('city', e.target.value)}
                placeholder="مثال: القاهرة" className="mt-1 rounded-xl" />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Details */}
      {step === 3 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-foreground">تفاصيل إضافية</h2>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">الرقم الضريبي (للفاتورة الإلكترونية)</label>
              <Input value={form.vat} onChange={e => set('vat', e.target.value)}
                placeholder="مثال: 123456789" className="mt-1 rounded-xl" dir="ltr" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">حد الائتمان ({cur()})</label>
              <Input value={form.credit_limit} onChange={e => set('credit_limit', e.target.value)}
                placeholder="0" type="number" min="0" className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">ملاحظات داخلية</label>
              <Textarea value={form.comment} onChange={e => set('comment', e.target.value)}
                placeholder="أي معلومات إضافية..." className="mt-1 rounded-xl" rows={3} />
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 4 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-foreground">مراجعة البيانات</h2>
          <div className="bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden">
            {[
              { label: 'الاسم', value: form.name },
              { label: 'الهاتف', value: form.phone },
              { label: 'الجوال', value: form.mobile },
              { label: 'البريد', value: form.email },
              { label: 'العنوان', value: [form.street, form.city].filter(Boolean).join('، ') },
              { label: 'الرقم الضريبي', value: form.vat },
              { label: 'حد الائتمان', value: form.credit_limit ? `${form.credit_limit} ${cur()}` : '' },
              { label: 'ملاحظات', value: form.comment },
            ].filter(r => r.value).map(r => (
              <div key={r.label} className="flex gap-3 px-4 py-3">
                <span className="text-xs text-muted-foreground w-28 flex-shrink-0 pt-0.5">{r.label}</span>
                <span className="text-sm font-medium text-foreground">{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border p-4 flex gap-2 z-30">
        {step > 1 && (
          <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">
            السابق
          </Button>
        )}
        {step < 4 ? (
          <Button
            className="flex-1 bg-primary"
            onClick={() => {
              if (step === 1 && !form.name.trim()) { toast.error('الاسم مطلوب'); return; }
              if (step === 1 && !form.phone.trim()) { toast.error('الهاتف مطلوب'); return; }
              setStep(s => s + 1);
            }}
          >
            التالي
          </Button>
        ) : (
          <Button onClick={submit} disabled={saving} className="flex-1 bg-primary gap-2">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري الإنشاء...</> : <><Check className="w-4 h-4" /> إنشاء العميل</>}
          </Button>
        )}
      </div>
    </div>
  );
}